import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { List, ListItem, Popover } from '@material-ui/core';
import { DatePicker } from '~/modules/common/components';
import { useMeContext } from '~/modules/me';
import {
  getLuxonJsDateFormatFromMe,
  mapIsoStringtoUtcObject
} from '~/modules/common/dates/convert';
import {
  getUserPreferenceWeekStart,
  getUserPreferenceWeekEnd
} from '~/modules/common/dates/dayOfWeek';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline/periodScale';
import { getDateRangeForScale } from '../useChartChangeHandlers';
import ChartDateRangeLabel from '../ChartDateRangeLabel';
import useDatePickerStyles from '../datePickerStyles';
import { getPickerProps } from '../datePickerUtils';
import ChartDateRangeQuarterPicker from './ChartDateRangeQuarterPicker';

const DEFAULT_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'left' };
const DEFAULT_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'left' };

export const ChartDateRangePicker = ({
  menuId,
  anchorEl,
  scale,
  dateRange,
  onClose,
  onChange,
  setDateValue
}) => {
  const isOpen = Boolean(anchorEl);
  const me = useMeContext();
  const classes = useDatePickerStyles();
  const weekStart = getUserPreferenceWeekStart(me, DateTime.local());
  const weekEnd = getUserPreferenceWeekEnd(me, DateTime.local());
  const {
    featureFlags: { isRmp2025Q3Enhancements }
  } = me;

  const todayDateRange = useMemo(
    () =>
      scale === PERIOD_SCALE_ENUM.WEEKS
        ? {
            startDate: weekStart,
            endDate: weekEnd
          }
        : {
            startDate: DateTime.local(),
            endDate: DateTime.local()
          },
    [scale, weekEnd, weekStart]
  );

  const luxonFormat = getLuxonJsDateFormatFromMe(me);

  const handleDateChange = useCallback(
    value => {
      if (onChange) {
        setDateValue
          ? onChange(value)
          : onChange(getDateRangeForScale({ me, date: value, scale }));
      }
      if (onClose) onClose();
    },
    [me, onChange, onClose, scale, setDateValue]
  );

  const handleTodayClick = useCallback(() => {
    if (onChange) onChange(todayDateRange);

    if (onClose) onClose();
  }, [onChange, onClose, todayDateRange]);

  const pickerProps = getPickerProps(me, scale, classes);

  return scale === PERIOD_SCALE_ENUM.QUARTERS ? (
    <ChartDateRangeQuarterPicker
      dateRange={dateRange}
      menuId={menuId}
      anchorEl={anchorEl}
      onChange={onChange}
      onClose={onClose}
      isRmp2025Q3Enhancements={isRmp2025Q3Enhancements}
    />
  ) : (
    <Popover
      id={menuId}
      anchorEl={anchorEl}
      anchorOrigin={DEFAULT_ANCHOR_ORIGIN}
      transformOrigin={DEFAULT_TRANSFORM_ORIGIN}
      open={isOpen}
      onClose={onClose}
    >
      <DatePicker
        {...pickerProps}
        autoOk
        variant="static"
        format={luxonFormat}
        value={mapIsoStringtoUtcObject(dateRange.startDate.toISODate())}
        onChange={handleDateChange}
      />
      <List disablePadding className={classes.quickLinksList}>
        <ListItem button onClick={handleTodayClick}>
          <ChartDateRangeLabel
            scale={scale}
            start={todayDateRange.startDate}
            end={todayDateRange.endDate}
          />
        </ListItem>
      </List>
    </Popover>
  );
};

ChartDateRangePicker.propTypes = {
  menuId: PropTypes.string.isRequired,
  scale: PropTypes.oneOf([
    PERIOD_SCALE_ENUM.DAYS,
    PERIOD_SCALE_ENUM.MONTHS,
    PERIOD_SCALE_ENUM.WEEKS,
    PERIOD_SCALE_ENUM.QUARTERS,
    PERIOD_SCALE_ENUM.YEARS
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  anchorEl: PropTypes.object,
  dateRange: PropTypes.object,
  setDateValue: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

export default ChartDateRangePicker;
